import { Fragment } from 'react';

import CseMachine from '../CseMachine';
import { Config } from '../CseMachineConfig';
import { Layout } from '../CseMachineLayout';
import type { Data } from '../CseMachineTypes';
import { isDummyKey, isMainReference } from '../CseMachineUtils';
import { ArrowFromText } from './arrows/ArrowFromText';
import { GenericArrow } from './arrows/GenericArrow';
import { Frame } from './Frame';
import { Text } from './Text';
import { ArrayValue } from './values/ArrayValue';
import { FnValue } from './values/FnValue';
import { GlobalFnValue } from './values/GlobalFnValue';
import { PrimitiveValue } from './values/PrimitiveValue';
import { UnassignedValue } from './values/UnassignedValue';
import { Value } from './values/Value';
import { Visible } from './Visible';

/** a `binding` is a key-value pair in a frame */
export class Binding extends Visible {
  /** value associated with this binding */
  readonly value: Value;
  /** key of this binding */
  readonly key: Text;
  /**
   * `true` if this is a dummy binding
   * i.e. the value is anonymous
   */
  readonly isDummyBinding: boolean = false;
  readonly keyYOffset: number;
  readonly printFnDescriptionHeight: number;

  /** arrow that is drawn from the key to the value */
  arrow?: GenericArrow<Text, Value>;

  constructor(
    /** the key of this binding */
    readonly keyString: string,
    /** the value of this binding */
    readonly data: Data,
    /** frame this binding is in */
    readonly frame: Frame,
    /** previous binding (the binding above it) */
    readonly prevBinding: Binding | null,
    readonly isConstant: boolean = false,
    readonly isLive: boolean = true,
  ) {
    super();
    this.isDummyBinding = isDummyKey(this.keyString);

    // derive the coordinates from the binding above it
    if (this.prevBinding) {
      this._x = this.prevBinding.x();
      this._y = this.prevBinding.y() + this.prevBinding.height() + Config.TextPaddingY;
    } else {
      this._x = this.frame.x() + Config.FramePaddingX;
      this._y = this.frame.y() + Config.FramePaddingY;
    }

    const GlobalDefaultText = this.keyString === Config.GlobalFrameDefaultText;
    const colon = isConstant ? 'constant' : 'variable';
    this.keyString += isConstant ? Config.ConstantColon : Config.VariableColon;
    this.value = Layout.createValue(data, this);

    const keyYOffset =
      this.value instanceof ArrayValue
        ? (Config.DataUnitHeight - Config.FontSize) / 2
        : (this.value.height() - Config.FontSize) / 2;

    this.keyYOffset = keyYOffset;
    const availableKeyWidth = GlobalDefaultText
      ? Config.FrameDefaultWidth - Config.FramePaddingX * 2 // for GlobalFrameDefaultText, use default frame width
      : (Config.FrameDefaultWidth - Config.TextPaddingX - Config.FramePaddingX * 2) / 2;

    this.key = new Text(this.keyString, this.x(), this.y() + keyYOffset, {
      maxWidth: availableKeyWidth,
      faded: !this.isLive,
      bindingType: colon,
      parentFrame: this.frame,
    });

    this.printFnDescriptionHeight =
      CseMachine.getPrintableMode() &&
      this.rendersReferencedValue() &&
      (this.value instanceof FnValue || this.value instanceof GlobalFnValue)
        ? this.value.printDescriptionHeight +
          this.value.printDescriptionOffsetY +
          this.value.printDescriptionBottomGap +
          Config.TextPaddingY / 2
        : 0;

    // derive the width from the right bound of the value
    this._width = this.rendersReferencedValue() ? this.value.x() - this.x() : this.key.width();

    this._height = Math.max(
      this.key.height(),
      this.rendersReferencedValue() && this.value instanceof ArrayValue
        ? this.value.totalHeight
        : this.rendersReferencedValue()
          ? this.value.height() + this.printFnDescriptionHeight
          : 0,
    );

    if (this.isDummyBinding && !isMainReference(this.value, this)) {
      if (this.prevBinding) {
        this._y = this.prevBinding.y();
        this._width = this.prevBinding.width();
        this._height = this.prevBinding.height();
      }
    }
  }

  private bindingIsLive(): boolean {
    return this.isDummyBinding ? (this.value?.isLive?.() ?? false) : this.frame.isLive;
  }

  public occupiesVerticalSpace(): boolean {
    return !Layout.clearDeadFrames || this.bindingIsLive();
  }

  public rendersReferencedValue(): boolean {
    if (!isMainReference(this.value, this)) {
      return false;
    }
    if (!Layout.clearDeadFrames) {
      return true;
    }
    // Keep function values interactive even when they are classified as dead.
    if (this.value instanceof FnValue || this.value instanceof GlobalFnValue) {
      return true;
    }
    return this.value?.isLive?.() ?? true;
  }

  /**
   * Reassigns the coordinates according to the final position of this frame
   * @param newX taken from cached layout
   */
  reassignCoordinates(newX: number, newY: number): void {
    if (this.prevBinding) {
      this._x = this.prevBinding.x();
      this._y = this.prevBinding.y() + this.prevBinding.height() + Config.TextPaddingY;
    } else {
      this._x = newX + Config.FramePaddingX;
      this._y = newY + Config.FramePaddingY;
    }
  }

  draw(): React.ReactNode {
    const isLive = this.bindingIsLive();
    const shouldRenderReferencedValue = this.rendersReferencedValue();

    if (Layout.clearDeadFrames && !isLive) {
      return shouldRenderReferencedValue ? this.value.draw() : null;
    }

    this.key.options.faded = !isLive;

    if (this.value instanceof PrimitiveValue || this.value instanceof UnassignedValue) {
      this.value.setFaded(!isLive);
    }

    // Update Text to new position
    (this.key as any)._x = this.x();
    (this.key as any)._y = this.y() + this.keyYOffset;

    if (
      !this.isDummyBinding && // value is unreferenced in dummy binding
      !(this.value instanceof PrimitiveValue) &&
      !(this.value instanceof UnassignedValue)
    ) {
      this.arrow = new ArrowFromText(this.key, this.frame).to(this.value);
    }

    return (
      <Fragment key={Layout.key++}>
        {this.isDummyBinding
          ? null // omit the key since value is anonymous
          : this.key.draw()}
        {this.arrow?.draw()}
        {shouldRenderReferencedValue ? this.value.draw() : null}
      </Fragment>
    );
  }
}
